import { cssTransition, toast } from 'react-toastify';

import PropTypes from 'prop-types';
import * as S from './toast.styles';
import Icon from '../icon/Icon';
import H from '../texts/h/H';
import P from '../texts/p/P';

const Fade = cssTransition({
  enter: 'Toastify__fade-enter',
  exit: 'Toastify__fade-exit',
});

const states = {
  info: {
    text: { variant: 'neutral', modifier: 'inverted' },
    icon: {
      name: 'info_circle_outline',
      variant: 'neutral',
      modifier: 'inverted',
    },
  },
  success: {
    text: { variant: 'neutral', modifier: 'inverted' },
    icon: {
      name: 'check_circle_outline',
      variant: 'neutral',
      modifier: 'inverted',
    },
  },
  warning: {
    text: { variant: 'neutral', modifier: 'strong' },
    icon: {
      name: 'error_circle_outline',
      variant: 'neutral',
      modifier: 'strong',
    },
  },
  danger: {
    text: { variant: 'neutral', modifier: 'inverted' },
    icon: {
      name: 'warning_triangle_outline',
      variant: 'neutral',
      modifier: 'inverted',
    },
  },
};

const renderIcon = ({ variant }) => (
  <Icon
    name={states[variant].icon.name}
    variant={states[variant].icon.variant}
    modifier={states[variant].icon.modifier}
    width={32}
    height={32}
  />
);

const renderToast = ({ title, description, variant }) => (
  <S.ToastBody>
    <div className='toast-body__icon'>{renderIcon({ variant: variant })}</div>
    <div className='toast-body__text'>
      {title && (
        <H
          variant={states[variant].text.variant}
          modifier={states[variant].text.modifier}
          type='6'
          withMargin={false}
        >
          {title}
        </H>
      )}
      {description && (
        <P
          variant={states[variant].text.variant}
          modifier={states[variant].text.modifier}
          type='2'
          weight='regular'
          withMargin={false}
        >
          {description}
        </P>
      )}
    </div>
  </S.ToastBody>
);

const useToast = {
  error: ({ title, id, description, closeButton, autoClose }) => {
    toast.error(renderToast({ title, description, variant: 'danger' }), {
      containerId: id,
      closeButton: closeButton && <CloseButton variant='danger' />,
      autoClose,
    });
  },
  success: ({ title, id, description, closeButton, autoClose }) => {
    toast.success(renderToast({ title, description, variant: 'success' }), {
      containerId: id,
      closeButton: closeButton && <CloseButton variant='success' />,
      autoClose,
    });
  },
  inProgress: ({ title, id, description, closeButton, autoClose }) => {
    toast.info(renderToast({ title, description, variant: 'info' }), {
      containerId: id,
      closeButton: closeButton && <CloseButton variant='info' />,
      autoClose,
    });
  },
  info: ({ title, id, description, closeButton, autoClose }) => {
    toast.info(renderToast({ title, description, variant: 'info' }), {
      containerId: id,
      closeButton: closeButton && <CloseButton variant='info' />,
      autoClose,
    });
  },
  warning: ({ title, id, description, closeButton, autoClose }) => {
    toast.warning(renderToast({ title, description, variant: 'warning' }), {
      containerId: id,
      closeButton: closeButton && <CloseButton variant='warning' />,
      autoClose,
    });
  },
  dismiss: () => {
    toast.dismiss();
  },
};

const CloseButton = ({ closeToast, variant }) => (
  <S.IconCloseWrapper>
    <Icon
      name='cancel_outline'
      variant={states[variant].icon.variant}
      modifier={states[variant].icon.modifier}
      cursor='pointer'
      onClick={closeToast}
      width={24}
      height={24}
    />
  </S.IconCloseWrapper>
);

const Toast = ({ position, autoClose, className, id, fullWidth }) => {
  return (
    <S.Toast
      enableMultiContainer
      containerId={id}
      position={position}
      autoClose={autoClose}
      hideProgressBar
      newestOnTop
      closeOnClick={false}
      rtl={false}
      pauseOnFocusLoss={false}
      draggable={false}
      pauseOnHover={true}
      transition={Fade}
      className={`${className} ${fullWidth && 'Toast__toast-body--md-full'}`}
      limit={3}
      closeButton={false}
    />
  );
};

Toast.defaultProps = {
  position: 'top-right',
  fullWidth: false,
  autoClose: 5000,
};

Toast.propTypes = {
  position: PropTypes.oneOf([
    'top-left',
    'top-center',
    'top-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ]),
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
};

export { Toast, useToast };
